/**
 * Keyboard Shortcut Manager
 *
 * Comprehensive keyboard shortcuts system with customization, conflict detection,
 * and cross-platform compatibility. Handles global shortcuts, context-aware bindings,
 * and user preferences with localStorage persistence.
 */

import {
  type ShortcutAction,
  type ShortcutBinding,
  type ShortcutConflict,
  type ShortcutPreferences,
  type KeyCombination,
  type ModifierKey,
  type Platform,
  type ShortcutContext,
} from '@/core/types/keyboard';

// Platform detection utility
function detectPlatform(): Platform {
  if (typeof navigator !== 'undefined') {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes('mac')) return 'mac';
    if (platform.includes('win')) return 'windows';
    return 'linux';
  }
  return 'linux'; // Default fallback
}

// Key combination matching utility
function keyCombinationsEqual(a: KeyCombination, b: KeyCombination): boolean {
  if (a.key.toLowerCase() !== b.key.toLowerCase()) return false;
  if (a.modifiers.length !== b.modifiers.length) return false;

  const aModifiers = [...a.modifiers].sort();
  const bModifiers = [...b.modifiers].sort();

  return aModifiers.every((mod, index) => mod === bModifiers[index]);
}

// Normalize modifier keys for platform
function normalizeModifiers(
  modifiers: ModifierKey[],
  platform: Platform
): ModifierKey[] {
  return modifiers.map((mod) => {
    // Convert platform-specific modifiers
    if (mod === 'meta' && platform !== 'mac') return 'ctrl';
    if (mod === 'cmd' && platform !== 'mac') return 'ctrl';
    if (mod === 'cmd' && platform === 'mac') return 'meta';
    return mod;
  });
}

// Event modifier checking utility
function eventMatchesKeys(
  event: KeyboardEvent,
  keys: KeyCombination,
  platform: Platform
): boolean {
  // Normalize event key
  const eventKey = event.key.toLowerCase();
  const targetKey = keys.key.toLowerCase();

  if (eventKey !== targetKey) return false;

  // Check modifiers
  const normalizedModifiers = normalizeModifiers(keys.modifiers, platform);

  const hasCtrl =
    normalizedModifiers.includes('ctrl') ||
    normalizedModifiers.includes('meta');
  const hasAlt = normalizedModifiers.includes('alt');
  const hasShift = normalizedModifiers.includes('shift');

  const eventHasCtrl = event.ctrlKey || event.metaKey;
  const eventHasAlt = event.altKey;
  const eventHasShift = event.shiftKey;

  return (
    hasCtrl === eventHasCtrl &&
    hasAlt === eventHasAlt &&
    hasShift === eventHasShift
  );
}

// Default preferences
const DEFAULT_PREFERENCES: ShortcutPreferences = {
  showInTooltips: true,
  showInMenus: true,
  enableGlobal: true,
  platform: 'auto',
  customBindings: {},
  disabledActions: [],
  helpDisplay: {
    enableHelpOverlay: true,
    showInCommandPalette: true,
    groupByCategory: true,
  },
};

export class KeyboardShortcutManager {
  private actions = new Map<string, ShortcutAction>();
  private bindings = new Map<string, ShortcutBinding>();
  private listeners = new Map<string, Set<(event: KeyboardEvent) => void>>();
  private preferences: ShortcutPreferences = DEFAULT_PREFERENCES;
  private platform: Platform;
  private currentContext: ShortcutContext = 'global';
  private isListening = false;

  constructor() {
    this.platform = detectPlatform();
    this.loadPreferences();
    this.setupGlobalListener();
  }

  /**
   * Register a shortcut action
   */
  registerAction(action: ShortcutAction): void {
    this.actions.set(action.id, action);

    // Create default binding if none exists
    if (!this.bindings.has(action.id)) {
      // Actions should define their default key combinations
      // This will be handled by the default shortcuts registry
    }
  }

  /**
   * Register multiple actions at once
   */
  registerActions(actions: ShortcutAction[]): void {
    actions.forEach((action) => this.registerAction(action));
  }

  /**
   * Create a key binding
   */
  createBinding(
    actionId: string,
    keys: KeyCombination,
    isCustom = false
  ): void {
    const action = this.actions.get(actionId);
    if (!action) {
      console.warn(`Keyboard shortcut: Action ${actionId} not found`);
      return;
    }

    // Check for conflicts
    const conflicts = this.detectConflicts(keys, actionId);
    if (conflicts.some((c) => c.severity === 'error')) {
      console.warn('Keyboard shortcut conflict detected:', conflicts);
      return;
    }

    const binding: ShortcutBinding = {
      actionId,
      keys,
      isCustom,
      createdAt: new Date(),
      modifiedAt: new Date(),
    };

    this.bindings.set(actionId, binding);

    if (isCustom) {
      this.preferences.customBindings[actionId] = keys;
      this.savePreferences();
    }
  }

  /**
   * Remove a key binding
   */
  removeBinding(actionId: string): void {
    this.bindings.delete(actionId);
    delete this.preferences.customBindings[actionId];
    this.savePreferences();
  }

  /**
   * Get all registered actions
   */
  getActions(): ShortcutAction[] {
    return Array.from(this.actions.values());
  }

  /**
   * Get all key bindings
   */
  getBindings(): ShortcutBinding[] {
    return Array.from(this.bindings.values());
  }

  /**
   * Get binding for specific action
   */
  getBinding(actionId: string): ShortcutBinding | undefined {
    return this.bindings.get(actionId);
  }

  /**
   * Get actions by category
   */
  getActionsByCategory(category: string): ShortcutAction[] {
    return Array.from(this.actions.values()).filter(
      (action) => action.category === category
    );
  }

  /**
   * Get actions by context
   */
  getActionsByContext(context: ShortcutContext): ShortcutAction[] {
    return Array.from(this.actions.values()).filter(
      (action) => action.context === context || action.context === 'global'
    );
  }

  /**
   * Set current context
   */
  setContext(context: ShortcutContext): void {
    this.currentContext = context;
  }

  /**
   * Detect shortcut conflicts
   */
  detectConflicts(
    keys: KeyCombination,
    excludeActionId?: string
  ): ShortcutConflict[] {
    const conflicts: ShortcutConflict[] = [];
    const conflictingActions: string[] = [];

    for (const [actionId, binding] of this.bindings) {
      if (actionId === excludeActionId) continue;

      if (keyCombinationsEqual(keys, binding.keys)) {
        conflictingActions.push(actionId);
      }

      // Check alternatives
      if (binding.keys.alternatives) {
        for (const alt of binding.keys.alternatives) {
          if (keyCombinationsEqual(keys, alt)) {
            conflictingActions.push(actionId);
          }
        }
      }
    }

    if (conflictingActions.length > 0) {
      // Determine severity based on context overlap
      const actions = conflictingActions.map((id) => this.actions.get(id)!);
      const hasOverlappingContext = actions.some(
        (action) =>
          action.context === 'global' ||
          actions.some((other) => other.context === action.context)
      );

      conflicts.push({
        keys,
        actions: conflictingActions,
        severity: hasOverlappingContext ? 'error' : 'warning',
        suggestion: hasOverlappingContext
          ? 'Choose a different key combination'
          : 'Conflicts may occur in overlapping contexts',
      });
    }

    return conflicts;
  }

  /**
   * Get all current conflicts
   */
  getAllConflicts(): ShortcutConflict[] {
    const conflicts: ShortcutConflict[] = [];
    const processed = new Set<string>();

    for (const [_actionId, binding] of this.bindings) {
      const keyStr = this.keyComboToString(binding.keys);
      if (processed.has(keyStr)) continue;

      const actionConflicts = this.detectConflicts(binding.keys);
      conflicts.push(...actionConflicts);
      processed.add(keyStr);
    }

    return conflicts;
  }

  /**
   * Execute action by ID
   */
  executeAction(actionId: string, event?: KeyboardEvent): void {
    const action = this.actions.get(actionId);
    if (!action) return;

    if (
      !action.enabled ||
      this.preferences.disabledActions.includes(actionId)
    ) {
      return;
    }

    // Check context
    if (action.context !== 'global' && action.context !== this.currentContext) {
      return;
    }

    try {
      action.handler(event);
    } catch (error) {
      console.error(`Error executing shortcut ${actionId}:`, error);
    }
  }

  /**
   * Enable/disable action
   */
  setActionEnabled(actionId: string, enabled: boolean): void {
    const action = this.actions.get(actionId);
    if (!action) return;

    action.enabled = enabled;

    if (enabled) {
      this.preferences.disabledActions =
        this.preferences.disabledActions.filter((id) => id !== actionId);
    } else {
      if (!this.preferences.disabledActions.includes(actionId)) {
        this.preferences.disabledActions.push(actionId);
      }
    }

    this.savePreferences();
  }

  /**
   * Get user preferences
   */
  getPreferences(): ShortcutPreferences {
    return { ...this.preferences };
  }

  /**
   * Update user preferences
   */
  updatePreferences(updates: Partial<ShortcutPreferences>): void {
    this.preferences = { ...this.preferences, ...updates };
    this.savePreferences();
  }

  /**
   * Convert key combination to display string
   */
  keyComboToString(keys: KeyCombination): string {
    const platform =
      this.preferences.platform === 'auto'
        ? this.platform
        : this.preferences.platform;
    const modifiers = normalizeModifiers(keys.modifiers, platform);

    const modifierSymbols: Record<ModifierKey, string> =
      platform === 'mac'
        ? {
            meta: '⌘',
            cmd: '⌘',
            ctrl: '⌃',
            alt: '⌥',
            shift: '⇧',
          }
        : {
            meta: 'Ctrl',
            cmd: 'Ctrl',
            ctrl: 'Ctrl',
            alt: 'Alt',
            shift: 'Shift',
          };

    const modifierParts = modifiers.map((mod) => modifierSymbols[mod]);
    const keyPart = keys.key.length === 1 ? keys.key.toUpperCase() : keys.key;

    return platform === 'mac'
      ? modifierParts.join('') + keyPart
      : modifierParts.join('+') +
          (modifierParts.length > 0 ? '+' : '') +
          keyPart;
  }

  /**
   * Parse string to key combination
   */
  stringToKeyCombo(str: string): KeyCombination | null {
    const platform =
      this.preferences.platform === 'auto'
        ? this.platform
        : this.preferences.platform;

    try {
      const parts =
        platform === 'mac'
          ? this.parseMacString(str)
          : str.split('+').map((p) => p.trim());

      const modifiers: ModifierKey[] = [];
      let key = '';

      for (const part of parts) {
        const lower = part.toLowerCase();
        if (['ctrl', 'control'].includes(lower)) {
          modifiers.push('ctrl');
        } else if (['cmd', 'command', '⌘'].includes(lower)) {
          modifiers.push(platform === 'mac' ? 'meta' : 'ctrl');
        } else if (['alt', 'option', '⌥'].includes(lower)) {
          modifiers.push('alt');
        } else if (['shift', '⇧'].includes(lower)) {
          modifiers.push('shift');
        } else {
          key = part;
        }
      }

      if (!key) return null;

      return { key: key.toLowerCase(), modifiers };
    } catch {
      return null;
    }
  }

  /**
   * Parse Mac-style shortcut string
   */
  private parseMacString(str: string): string[] {
    const parts: string[] = [];
    let current = '';

    for (const char of str) {
      if (['⌘', '⌃', '⌥', '⇧'].includes(char)) {
        if (current) {
          parts.push(current);
          current = '';
        }
        parts.push(char);
      } else {
        current += char;
      }
    }

    if (current) {
      parts.push(current);
    }

    return parts;
  }

  /**
   * Setup global keyboard event listener
   */
  private setupGlobalListener(): void {
    if (typeof window === 'undefined' || this.isListening) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle if typing in input elements
      const target = event.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.contentEditable === 'true')
      ) {
        return;
      }

      // Find matching actions
      const matchingActions = this.getActionsForEvent(event);

      if (matchingActions.length > 0) {
        // Execute highest priority action
        const action = matchingActions.sort((a, b) => {
          const priorities = { system: 4, high: 3, normal: 2, low: 1 };
          return priorities[b.priority] - priorities[a.priority];
        })[0];

        event.preventDefault();
        event.stopPropagation();
        this.executeAction(action.id, event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    this.isListening = true;
  }

  /**
   * Get actions that match keyboard event
   */
  private getActionsForEvent(event: KeyboardEvent): ShortcutAction[] {
    const matchingActions: ShortcutAction[] = [];

    for (const [actionId, binding] of this.bindings) {
      const action = this.actions.get(actionId);
      if (!action || !action.enabled) continue;

      if (this.preferences.disabledActions.includes(actionId)) continue;

      // Check context
      if (
        action.context !== 'global' &&
        action.context !== this.currentContext
      ) {
        continue;
      }

      // Check key match
      if (eventMatchesKeys(event, binding.keys, this.platform)) {
        matchingActions.push(action);
        continue;
      }

      // Check alternatives
      if (binding.keys.alternatives) {
        for (const alt of binding.keys.alternatives) {
          if (eventMatchesKeys(event, alt, this.platform)) {
            matchingActions.push(action);
            break;
          }
        }
      }
    }

    return matchingActions;
  }

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem('phrasewright-shortcuts-preferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.preferences = { ...DEFAULT_PREFERENCES, ...parsed };

        // Apply custom bindings
        for (const [actionId, keys] of Object.entries(
          this.preferences.customBindings
        )) {
          this.createBinding(actionId, keys, true);
        }
      }
    } catch (error) {
      console.warn('Failed to load keyboard shortcut preferences:', error);
    }
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem(
        'phrasewright-shortcuts-preferences',
        JSON.stringify(this.preferences)
      );
    } catch (error) {
      console.warn('Failed to save keyboard shortcut preferences:', error);
    }
  }

  /**
   * Reset all preferences to defaults
   */
  resetPreferences(): void {
    this.preferences = { ...DEFAULT_PREFERENCES };
    this.bindings.clear();
    this.savePreferences();
    // Re-register default bindings would happen here
  }

  /**
   * Dispose of the manager
   */
  dispose(): void {
    // Clean up event listeners if needed
    this.actions.clear();
    this.bindings.clear();
    this.listeners.clear();
  }
}

// Singleton instance
export const keyboardManager = new KeyboardShortcutManager();
